/**
 * Bridge the store's pointer to the browser URL.
 *
 * We use the hash fragment rather than the path because:
 *  - Bliksys is a pure SPA and often hosted at a sub-path; the hash survives
 *    that without any server config.
 *  - Pointers contain slashes, which would require URL-encoding in the path
 *    but render cleanly as-is in the hash.
 *
 * The pointer IS the URL fragment after `#`:
 *   root          ->  #
 *   /pages/0      ->  #/pages/0
 *
 * Escape the JSON-pointer escapes once more for URL safety (~ and / are
 * already escaped at the pointer level, but the browser likes `%` too).
 */

import { useEffect } from 'react';

import type { Store } from '../store.js';

export function useUrlSync(store: Store): void {
  // On mount: seed the store from the URL if present.
  useEffect(() => {
    const fromUrl = pointerFromHash(window.location.hash);
    if (fromUrl === null) return;
    if (fromUrl !== store.getState().pointer) {
      store.navigate(fromUrl);
    }
    // If we're restoring a non-root pointer from the URL, also begin a
    // draft at that pointer — same affordance the user gets when they
    // navigate into a record by clicking. Without this, reloading a deep
    // page leaves the user without Apply/Discard buttons because
    // beginDraft only fires from `zoomInto` (a click handler) and never
    // from URL restoration.
    if (fromUrl !== '' && store.getState().draftPointer === null) {
      store.beginDraft(fromUrl, 'edit');
    }
    // We deliberately depend only on `store` — if the store instance changes
    // we re-seed; otherwise this runs once. ESLint can't see that because
    // store.getState isn't a reactive dep.
  }, [store]);

  // Whenever the store pointer changes, reflect it into the URL.
  useEffect(() => {
    return store.subscribe(() => {
      const current = store.getState().pointer;
      const target = hashFromPointer(current);
      if (window.location.hash !== target) {
        // pushState so Back/Forward work. Use replaceState for the seed case
        // to avoid polluting history with the initial render — but after
        // mount we always push.
        window.history.pushState({ pointer: current }, '', target);
      }
    });
  }, [store]);

  // React to Back/Forward: read the URL and navigate the store accordingly.
  // When a draft is in flight, treat browser back as Discard — without
  // this, the draft sub-tree would persist in the document with the
  // user no longer aware of it. Showing a modal here is fragile because
  // the popstate event has already fired; cleanest is to commit-or-discard
  // before honoring the URL.
  useEffect(() => {
    function onPop(): void {
      const fromUrl = pointerFromHash(window.location.hash);
      if (fromUrl === null) return;
      const draft = store.getState().draftPointer;
      if (draft !== null && fromUrl !== draft && !fromUrl.startsWith(`${draft}/`)) {
        // Browser back/forward going outside the draft. Discard wins —
        // the in-flight item is removed and the navigation honored.
        store.discardDraft();
        // discardDraft already navigates to the draft's parent; if the
        // URL points elsewhere, follow up.
        if (store.getState().pointer !== fromUrl) store.navigate(fromUrl);
        return;
      }
      store.navigate(fromUrl);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [store]);
}

function pointerFromHash(hash: string): string | null {
  if (!hash || hash === '#') return '';
  if (!hash.startsWith('#')) return null;
  const raw = hash.slice(1);
  try {
    const decoded = decodeURI(raw);
    // Pointer must start with '/' or be empty (root). Anything else is a
    // stray hash we shouldn't touch.
    if (decoded === '' || decoded.startsWith('/')) return decoded;
    return null;
  } catch {
    return null;
  }
}

function hashFromPointer(pointer: string): string {
  if (pointer === '') return '#';
  // encodeURI preserves '/' which is what we want for readable URLs.
  return '#' + encodeURI(pointer);
}
